import React, { useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import {
  pageFilterAtom,
  rowsPerPageFilterAtom,
  simProductsSortingAtomWithStorage,
} from 'atoms/filters/sim-products';
import { TableLayoutWithoutScrollArea } from 'src/common/components/Table/TableLayoutWithoutScrollArea';
import { ActivationDialog } from '../Actions/ActivationDialog';
import { DeactivationDialog } from '../Actions/DeactivationDialog';
import { Action } from 'common/components/ConfigurableTable/models/configurableTable';
import { useLocation, useNavigate } from 'react-router';
import { Routes } from 'src/Routes.constants';
import { SimProductsInventoryEditDialog } from './EditDialog/SimProductsInventoryEditDialog';
import { Actions, Subjects } from 'permissions/ability';
import { useAbility } from 'permissions/hooks/useAbility';
import { ACTIVATION_ACTIONS } from 'src/common/components/Products/actions/constants/activationActionsConstants';
import { useSimProductsColumnActions } from './ConfigurableTable/hooks/useSimProductsColumnActions';
import { useSimProductsConfigurationActions } from './ConfigurableTable/hooks/useSimProductsConfigurationActions';
import { TABLE_VARIANT } from 'src/common/components/Sim/Inventory/constants/columnConfigurationsConstants';
import { SimTable } from 'src/common/components/Sim/Inventory/SimTable';
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';

import { useExternalRowSelectionApi } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi';
import { useSimProductInventory } from '../hooks/useSimProductInventory';
import { useIsSimInLifeChangesAvailable } from 'products/sim/Actions/hooks/useIsSimInLifeChangesAvailable.ts';

export const SimProductsTable = React.memo(() => {
  const { selectedIds, setSelectedIds, unSelectAll } = useExternalRowSelectionApi();

  const ability = useAbility();
  const tagsEnabled = ability.can(Actions.edit, Subjects.tags);
  const customFieldsEnabled = ability.can(Actions.read, Subjects.customFields);

  const [sorting, setSorting] = useAtom(simProductsSortingAtomWithStorage);

  const [rowsPerPage, setRowsPerPage] = useAtom(rowsPerPageFilterAtom);
  const [page, setPage] = useAtom(pageFilterAtom);

  const inlifeChangesEnabled = useIsSimInLifeChangesAvailable();
  const {
    inventory: { simProducts, totalNumberOfItems },
    isRefetching,
  } = useSimProductInventory();

  useEffect(() => {
    if (isRefetching) {
      unSelectAll();
    }
  }, [isRefetching]);

  const { configuration } = simProducts;

  const { updateVariant, revertToDefault } = useSimProductsConfigurationActions(configuration);
  const { flatColumnConfigurations, combinedColumnConfigurations, enabledVariant } = configuration;

  const { updateWidthOfColumn, reorder, updateVisibilityOfColumns } =
    useSimProductsColumnActions(configuration);

  const onSortModelChange = useCallback((model: GridSortModel) => {
    const [sortField] = model;
    if (!sortField) {
      return;
    }

    setSorting({
      columnName: sortField.field,
      sort: sortField.sort ?? null,
    });
  }, []);

  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const actions: Action[] = useMemo(
    () => [
      {
        id: 'viewDetails',
        label: 'View Details',
        type: 'custom',
        actionCallback: (data: any) => {
          navigate(`${Routes.productsSim}/${data.iccid}`, {
            state: {
              previousPath: `${pathname}${search}`,
            },
          });
        },
      },
      {
        id: 'activation',
        label: 'Activate',
        type: 'dialog',
        Component: ActivationDialog,
        enabled: (data) => {
          return data.activationAction === ACTIVATION_ACTIONS.ACTIVATE && !inlifeChangesEnabled;
        },
        permission: {
          action: Actions.activation,
          subject: Subjects.simProducts,
        },
      },
      {
        id: 'deactivation',
        label: 'Deactivate',
        type: 'dialog',
        Component: DeactivationDialog,
        enabled: (data) => {
          return data.activationAction === ACTIVATION_ACTIONS.DEACTIVATE && !inlifeChangesEnabled;
        },
        permission: {
          action: Actions.activation,
          subject: Subjects.simProducts,
        },
      },
      {
        id: 'edit',
        label: 'Edit',
        type: 'dialog',
        Component: SimProductsInventoryEditDialog,
        enabled: () => tagsEnabled || customFieldsEnabled,
      },
    ],
    [tagsEnabled, customFieldsEnabled],
  );

  const onPaginationChange = useCallback(
    (pagination: GridPaginationModel) => {
      if (pagination.page !== page) {
        unSelectAll();
        setPage(pagination.page);
      }

      if (pagination.pageSize !== rowsPerPage) {
        setRowsPerPage(pagination.pageSize);
      }
    },
    [page, rowsPerPage, setPage, setRowsPerPage, unSelectAll],
  );

  const onRowSelectionModelChange = useCallback((rowSelctionModel: GridRowSelectionModel) => {
    setSelectedIds(rowSelctionModel.ids);
  }, []);

  return (
    <TableLayoutWithoutScrollArea
      fetching={isRefetching}
      table={
        <SimTable
          selectedIds={inlifeChangesEnabled ? selectedIds : undefined}
          data-testid="sims inventory table"
          configuration={
            enabledVariant === TABLE_VARIANT.Combined
              ? combinedColumnConfigurations
              : flatColumnConfigurations
          }
          enabledVariant={enabledVariant}
          sort={sorting}
          onSortModelChange={onSortModelChange}
          updateWidthOfColumn={updateWidthOfColumn}
          reorder={reorder}
          updateVisibilityOfColumns={updateVisibilityOfColumns}
          updateVariant={updateVariant}
          revertToDefault={revertToDefault}
          rows={simProducts.items}
          actions={actions}
          page={page}
          pageSize={rowsPerPage}
          rowCount={totalNumberOfItems}
          onPaginationChange={onPaginationChange}
          onRowSelectionModelChange={onRowSelectionModelChange}
        />
      }
    />
  );
});
